import { ClassNames } from '@emotion/core'
import styled from '@emotion/styled/macro'
import { EmptyResults } from 'components/EmptyResults'
import useSettings from 'hooks/useSettings'
import { isEqual } from 'lodash-es'
import {
  CheckboxVisibility,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDetailsHeaderProps,
  IDetailsListProps,
  IDetailsRowProps,
  IGroup,
  IRenderFunction,
  ISelection,
  MarqueeSelection,
  Selection,
  SelectionMode,
  Sticky,
  StickyPositionType,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { Theme } from 'theme'
import {
  ensureGroupSortColumns,
  groupSortByColumn,
  groupSortByColumnAndParent,
} from './helpers'

const StyledDetailsList = styled(DetailsList)`
  .ms-DetailsRow-cell {
    color: ${({ theme }) => theme.palette.neutralPrimaryAlt};
  }
  .ms-DetailsRow {
    border-bottom: 0.5px solid #303539;
  }
`

interface ListSettings {
  compact: boolean
}

const EntityListNoCommandSettings: React.FunctionComponent<{
  children: (settings: ListSettings) => JSX.Element
}> = ({ children }) => {
  const [listSettings] = useSettings<ListSettings>('list', {
    compact: true,
  })
  return children(listSettings)
}

export interface EntityListState<T> {
  sortedItems: T[]
  columns: IColumn[]
  groups?: IGroup[]
}

export type EntityListProps<T> = Omit<IDetailsListProps, 'items'> & {
  items: T[]
  sortHighestGroup?: boolean
  alternateRowColor?: boolean
  stickyHeader?: boolean
  disableMarqueeSelection?: boolean
  isSearchResult?: boolean
  readOnly?: boolean
  disableListStyleToggle?: boolean
  parentGroup?: string
  noParentGroupText?: string
  alwaysCollapsed?: boolean
  onSelectionChanged?: (selection: T[]) => void
}

export class EntityList<T = any> extends React.Component<
  EntityListProps<T>,
  EntityListState<T>
> {
  private selection: ISelection

  constructor(props: EntityListProps<T>) {
    super(props)

    this.state = this.deriveState({}, props)

    this.selection =
      props.selection ||
      new Selection({
        selectionMode: props.selectionMode || SelectionMode.multiple,
        onSelectionChanged: this.handleSelectionChanged(
          props.onSelectionChanged
        ),
      })
  }

  public UNSAFE_componentWillReceiveProps(newProps: EntityListProps<T>) {
    if (newProps.selection && this.selection !== newProps.selection) {
      this.selection = newProps.selection
    }
    if (
      !isEqual(this.props.items, newProps.items) ||
      !isEqual(this.props.columns, newProps.columns)
    ) {
      this.setState(
        this.deriveState(
          {
            sortColumn: this.state.columns.filter(
              (column) => column.isSorted
            )[0],
            groupColumn: this.state.columns.filter(
              (column) => column.isGrouped
            )[0],
          },
          newProps
        )
      )
    }
  }

  public render(): JSX.Element {
    const {
      onSelectionChanged,
      items,
      alternateRowColor,
      disableMarqueeSelection,
      stickyHeader,
      readOnly,
      disableListStyleToggle,
      ...listProps
    } = this.props

    return (
      <>
        <MarqueeSelection
          selection={this.selection}
          isEnabled={
            !disableMarqueeSelection &&
            !readOnly &&
            this.selection.mode === SelectionMode.multiple
          }
        >
          <EntityListNoCommandSettings>
            {(listSettings) =>
              this.renderDetailsList(listSettings, listProps, readOnly)
            }
          </EntityListNoCommandSettings>
        </MarqueeSelection>
        {this.state.sortedItems.length === 0 && (
          <EmptyResults isSearchResult={this.props.isSearchResult} />
        )}
      </>
    )
  }

  private groupGroupsByName = (groups: IGroup[]) => {
    function compare(a: IGroup, b: IGroup) {
      if (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase()) {
        return -1
      }
      if (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) {
        return 1
      }
      return 0
    }
    groups.sort(compare)
    return groups
  }

  private renderDetailsList = (
    listSettings: ListSettings,
    listProps: Partial<EntityListProps<T>>,
    readOnly: boolean | undefined
  ) => (
    <StyledDetailsList
      layoutMode={DetailsListLayoutMode.justified}
      isHeaderVisible={true}
      setKey="entities"
      styles={{
        root: { color: 'black' },
      }}
      onRenderRow={this.handleRenderRow}
      onRenderDetailsHeader={this.handleRenderHeader}
      compact={listSettings.compact}
      groups={
        this.state.groups
          ? this.props.sortHighestGroup
            ? this.groupGroupsByName(this.state.groups)
            : this.state.groups
          : undefined
      }
      onColumnHeaderClick={this.handleColumnHeaderClick}
      checkboxVisibility={
        readOnly === true
          ? CheckboxVisibility.hidden
          : CheckboxVisibility.always
      }
      items={this.state.sortedItems}
      columns={this.state.columns}
      selection={this.selection}
      {...listProps}
    />
  )

  private deriveState = (
    overwrites: {
      sortColumn?: IColumn
      groupColumn?: IColumn
    } = {},
    props?: EntityListProps<T>
  ) => {
    const { columns: maybeColumns, items } = props || this.props
    const columns = maybeColumns || []

    const sortColumn =
      overwrites.sortColumn || columns.find((column) => !!column.isSorted)
    const groupColumn =
      overwrites.groupColumn || columns.find((column) => !!column.isGrouped)

    const { groups, sortedItems } = this.props.parentGroup
      ? groupSortByColumnAndParent(
          items,
          this.props.alwaysCollapsed === true ? true : false,
          this.props.parentGroup,
          this.props.noParentGroupText,
          groupColumn,
          sortColumn
            ? {
                ...sortColumn,
                isSortedDescending: sortColumn.isSortedDescending,
              }
            : sortColumn
        )
      : groupSortByColumn(
          items,
          this.props.alwaysCollapsed === true ? true : false,
          groupColumn,
          sortColumn
            ? {
                ...sortColumn,
                isSortedDescending: sortColumn.isSortedDescending,
              }
            : sortColumn
        )

    return {
      sortedItems,
      groups,
      columns: ensureGroupSortColumns(columns, groupColumn, sortColumn),
    }
  }

  private handleRenderHeader: IRenderFunction<IDetailsHeaderProps> = (
    props,
    defaultRender
  ) => {
    const { stickyHeader } = this.props

    const header = defaultRender!(props!)

    return stickyHeader ? (
      <Sticky stickyPosition={StickyPositionType.Header}>{header}</Sticky>
    ) : (
      header
    )
  }

  private handleRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => {
    const { alternateRowColor } = this.props

    if (alternateRowColor && props!.itemIndex % 2 === 1) {
      return (
        <ClassNames>
          {({ css: styles, cx, theme }) =>
            defaultRender!({
              ...props!,
              className: cx(
                props!.className,
                styles({
                  backgroundColor: (theme as Theme).palette.neutralLighterAlt,
                })
              ),
            })
          }
        </ClassNames>
      )
    }
    return defaultRender!(props)
  }

  private handleColumnHeaderClick = (ev: any, column?: IColumn): void => {
    if (column) {
      const data = column.data || {}
      const isGroupClick = ev.getModifierState('Alt')
      const sortingDescending: { isSortedDescending?: boolean } = {}
      if (column.isSorted !== false) {
        sortingDescending.isSortedDescending = column.isSorted
          ? !column.isSortedDescending
          : false
      }
      const overwrites = {
        groupColumn:
          isGroupClick && !data.ungroupable
            ? {
                ...column,
                isGrouped: !column.isGrouped,
              }
            : undefined,
        sortColumn: !isGroupClick
          ? {
              ...column,
              isSorted: column.isSorted === false ? false : true,
              ...sortingDescending,
            }
          : undefined,
      }
      const derivedState = this.deriveState(overwrites)

      this.setState(derivedState)
    }

    if (this.props.onColumnHeaderClick) {
      this.props.onColumnHeaderClick!(ev, column)
    }
  }

  private handleSelectionChanged = (callback: any) => () => {
    if (callback) {
      const selection = this.selection.getSelection()

      callback(selection)
    }
  }
}
